import React from 'react';
import { Button, Typography, Card, Flex, theme } from 'antd';
import { ChargeBeePlanId } from '@prerender/billing-shared';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import Plan from './Plan';
import StarterTrialActivationChoice from './StarterTrialActivationChoice';
import { isStarterTrialKeepPlanScenario } from './starterTrialActivation';
import { usePaymentChargebeeInfo } from '../hooks/usePaymentChargebeeInfo';
import { InviteTeamMember } from '../../teams';
import { useUserRole } from '../../../hooks/usePrerenderUser';
import { useSubscriptionState } from '../PlanStatus';
import { useTrial } from '~features/FreeTrial/useTrial';

type BillingChangePlanConfirmModalProps = {
  pendingNewPlan: Plan;
  currentPlan: Plan;
  onConfirmPlanChange: (oldPlanId: ChargeBeePlanId, newPlanId: ChargeBeePlanId, activateNow?: boolean) => void;
  onClose: () => void;
  isLoading?: boolean;
  scheduledPlanName?: string;
  // Starter-trial activation only. False when the user already had payment on file when the confirm
  // opened (a return visit) — then the modal offers only "Activate now" and drops "Activate after
  // trial". True on the first visit (adding payment now), so both choices stay (PRE-2726 QA).
  showActivateAfterTrial?: boolean;
};
function BillingChangePlanConfirmModal({
  pendingNewPlan,
  currentPlan,
  onConfirmPlanChange,
  onClose,
  isLoading = false,
  scheduledPlanName,
  showActivateAfterTrial = true,
}: BillingChangePlanConfirmModalProps) {
  const { onChangeBillingAddress, onChangePaymentMethod } = usePaymentChargebeeInfo();

  const userRole = useUserRole();
  const { isInTrial } = useTrial();
  const [planState, paymentMethodState] = useSubscriptionState();
  const { token } = theme.useToken();
  const sectionStyle = { marginTop: token.marginXL };

  const isSamePlan = pendingNewPlan.id === currentPlan.id;
  const isStarterTrialActivationChoice = isStarterTrialKeepPlanScenario({
    oldPlanId: currentPlan.id,
    newPlanId: pendingNewPlan.id,
    isInTrial,
  });
  const isUpgrading = pendingNewPlan.index > currentPlan.index;
  const isDowngrading = !isSamePlan && !isUpgrading;
  const isSubscriptionCancelled = planState.status === 'cancelled';
  const hasScheduledCancellation = planState.status === 'cancellation_scheduled';
  const isScheduledDowngrade = isDowngrading && !isSubscriptionCancelled;
  const shouldShowScheduledUpgradeWarning = Boolean(scheduledPlanName && (isUpgrading || isScheduledDowngrade));
  const scheduledPlanReplacementCopy = isDowngrading
    ? `Confirming this action will replace it with a scheduled downgrade to ${pendingNewPlan.name} at the end of the current billing period.`
    : `Confirming this action will remove that scheduled upgrade and replace it with an immediate plan change to ${pendingNewPlan.name}.`;
  const shouldShowScheduledCancellationWarning = hasScheduledCancellation && isScheduledDowngrade;

  return (
    <div style={{ maxWidth: 600, fontSize: token.fontSizeLG, marginTop: token.marginLG }}>
      <Typography.Title level={4}>Confirm Your Plan</Typography.Title>

      {isUpgrading && (
        <>
          <Typography.Title level={5}>Thank you for choosing the “{pendingNewPlan.name}” plan!</Typography.Title>
          <Typography.Paragraph strong>
            You are currently on {isInTrial ? 'Free Trial of the' : 'the'} {currentPlan.name} plan. Upgrading will take
            effect immediately and move you to {pendingNewPlan.name}.
          </Typography.Paragraph>
        </>
      )}
      {isScheduledDowngrade && (
        <Typography.Paragraph strong>
          You are currently on {isInTrial ? 'Free Trial of the' : 'the'} {currentPlan.name} plan. Downgrading will be
          scheduled for the end of your current billing period. You will keep {currentPlan.name} until then, and{' '}
          {pendingNewPlan.name} will start at renewal.
        </Typography.Paragraph>
      )}
      {isDowngrading && isSubscriptionCancelled && (
        <Typography.Paragraph strong>
          Your {currentPlan.name} plan is currently cancelled. Reactivating {pendingNewPlan.name} will restore access
          immediately and resume billing.
        </Typography.Paragraph>
      )}
      {shouldShowScheduledUpgradeWarning && (
        <Typography.Paragraph>
          You already have a scheduled plan change to <Typography.Text strong>{scheduledPlanName}</Typography.Text>.{' '}
          {scheduledPlanReplacementCopy}
        </Typography.Paragraph>
      )}
      {shouldShowScheduledCancellationWarning && (
        <Typography.Paragraph>
          You already have a scheduled cancellation. Confirming this action will remove that cancellation and replace it
          with a scheduled downgrade to {pendingNewPlan.name} at the end of the current billing period.
        </Typography.Paragraph>
      )}

      {isSamePlan && (
        <>
          <Typography.Title level={5}>
            {isStarterTrialActivationChoice
              ? `Activate your ${currentPlan.name} plan`
              : planState.status === 'cancelled'
              ? 'Reactivate your plan'
              : 'Keep access to your plan'}
          </Typography.Title>

          {isStarterTrialActivationChoice ? (
            <StarterTrialActivationChoice planName={currentPlan.name} showAfterTrial={showActivateAfterTrial} />
          ) : planState.status === 'cancelled' ? (
            <Typography.Paragraph strong>
              Your {currentPlan.name} plan is currently cancelled. Reactivating it will restore access immediately and
              resume billing.
            </Typography.Paragraph>
          ) : (
            <Typography.Paragraph>
              Make sure your payment method is set to continue accessing your plan.
            </Typography.Paragraph>
          )}
        </>
      )}

      {planState.status === 'cancelled' && (
        <>
          <Typography.Paragraph>
            You will be charged the prorated amount now, plus past unpaid invoices and overages, if any.
          </Typography.Paragraph>
          <Typography.Paragraph>
            At the end of the billing period, you will be charged the total monthly fee plus overages, if any, until you
            change or cancel your plan.
          </Typography.Paragraph>
        </>
      )}

      <Typography.Paragraph>Want your billing team to handle it? Invite your coworkers.</Typography.Paragraph>
      <InviteTeamMember
        source="Billing Change Plan Confirm Modal"
        style={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}
        selectRoles={true}
        userRole={userRole}
        buttonLabel="Invite Your Billing Manager"
      />
      <div style={sectionStyle}>
        <Card
          title="Payment Method"
          loading={paymentMethodState.isLoading}
          size="small"
          extra={
            !paymentMethodState.isLoading &&
            (paymentMethodState.isCreditCardValid ? (
              <CheckCircleOutlined style={{ color: token.colorSuccess }} />
            ) : (
              <ExclamationCircleOutlined style={{ color: token.colorError }} />
            ))
          }
        >
          <Flex justify="space-between" align="center">
            <div>
              {paymentMethodState.isCreditCardValid && <Typography.Text>A payment method is set.</Typography.Text>}
              {!paymentMethodState.isCreditCardValid && (
                <Typography.Text type="danger">
                  {!paymentMethodState.isPaymentMethodSet
                    ? 'You need to add a payment method to proceed.'
                    : 'Your payment method is invalid, please check your card details.'}
                </Typography.Text>
              )}
            </div>
            <Button onClick={onChangePaymentMethod} type={paymentMethodState.isCreditCardValid ? 'default' : 'primary'}>
              {!paymentMethodState.isPaymentMethodSet ? 'Add' : 'Change'}
            </Button>
          </Flex>
        </Card>
      </div>
      <div style={sectionStyle}>
        <Card
          title="Billing Address"
          loading={paymentMethodState.isLoading}
          size="small"
          extra={
            !paymentMethodState.isLoading &&
            (paymentMethodState.isAddressValid ? (
              <CheckCircleOutlined style={{ color: token.colorSuccess }} />
            ) : (
              <ExclamationCircleOutlined style={{ color: token.colorError }} />
            ))
          }
        >
          <Flex justify="space-between" align="center">
            <div>
              {paymentMethodState.isAddressValid ? (
                <Typography.Text>A billing address is set.</Typography.Text>
              ) : (
                <Typography.Text type="danger">You need to add a billing address to proceed.</Typography.Text>
              )}
            </div>
            <Button onClick={onChangeBillingAddress} type={paymentMethodState.isAddressValid ? 'default' : 'primary'}>
              {!paymentMethodState.isAddressValid ? 'Add' : 'Change'}
            </Button>
          </Flex>
        </Card>
      </div>
      <Flex justify="space-between" style={{ ...sectionStyle, fontWeight: token.fontWeightStrong }}>
        <span>Price</span> <span>{`$${pendingNewPlan.costInCents / 100}`}</span>
      </Flex>
      <hr />
      <Flex justify="space-between" style={{ ...sectionStyle, paddingBottom: token.paddingXL }}>
        <Button style={{ fontSize: token.fontSizeLG }} onClick={onClose} type="default" disabled={isLoading}>
          {/* "Back" for the trial activation choice: dismissing only returns to the previous page — it
              doesn't discard an already-saved payment method, which "Cancel" wrongly implied (PRE-2726 QA). */}
          {isStarterTrialActivationChoice ? 'Back' : 'Cancel'}
        </Button>
        {isStarterTrialActivationChoice ? (
          // Both paid-activation choices require valid payment details (Activate after trial schedules the
          // paid activation for trial end), so both are gated on isChargeable (PRE-2726 QA).
          <Flex gap={token.marginSM}>
            {showActivateAfterTrial && (
              <Button
                onClick={() => onConfirmPlanChange(currentPlan.id, pendingNewPlan.id)}
                type="default"
                loading={isLoading}
                disabled={!paymentMethodState.isChargeable}
              >
                Activate after trial
              </Button>
            )}
            <Button
              onClick={() => onConfirmPlanChange(currentPlan.id, pendingNewPlan.id, true)}
              type="primary"
              loading={isLoading}
              disabled={!paymentMethodState.isChargeable}
            >
              Activate now
            </Button>
          </Flex>
        ) : (
          <Button
            onClick={() => onConfirmPlanChange(currentPlan.id, pendingNewPlan.id)}
            type="primary"
            style={{ marginLeft: token.marginXS }}
            loading={isLoading}
            disabled={!paymentMethodState.isChargeable}
          >
            {isUpgrading && `Upgrade to ${pendingNewPlan.name}`}
            {isScheduledDowngrade && `Schedule downgrade to ${pendingNewPlan.name}`}
            {isDowngrading && isSubscriptionCancelled && `Reactivate ${pendingNewPlan.name}`}
            {isSamePlan &&
              (planState.status === 'cancelled'
                ? `Reactivate ${pendingNewPlan.name}`
                : `Keep access to ${pendingNewPlan.name}`)}
          </Button>
        )}
      </Flex>
    </div>
  );
}

export default BillingChangePlanConfirmModal;
